// apps/web/src/pages/Public/MagicLink/components/ClicksignEmbedWidget.tsx
//
// Phase 11 docs-signature-payment Plan 10 Task 1 — Clicksign Embedded Widget.
//
// Carrega o SDK Clicksign v2.1 via CDN lazy, inicializa o widget
// (`new window.Clicksign(requestSignatureKey).mount(containerId)`) e bind dos
// 3 events principais: `loaded` | `signed` | `error`.
//
// Pre-requisitos (cumpridos por Pedro fora do código — ver SUMMARY Task 3):
//   1. Clicksign sandbox token + HMAC em AWS Secrets Manager
//   2. Domínio `*.escritorioalamo.com.br` cadastrado no painel Clicksign
//      (CORS whitelist do widget).
//
// Threat references (PLAN.md <threat_model>):
//   - T-11-10-01 Tampering CDN: URL hardcoded versionada (`embedded.min-1.0.0.js`).
//     Iframe Clicksign roda em origin diferente (`https://app.clicksign.com` ou
//     `https://sandbox.clicksign.com`) → isolation por SOP. SRI hash header é
//     melhoria Phase futura.
//
// Fix B-NEW-4 (2026-05-20):
//   O SDK Embedded v2.1 expõe `widget.endpoint` antes de `mount()`. O default
//   hardcoded interno do SDK é `https://app.clicksign.com` (produção). Quando o
//   backend roda contra sandbox (`CLICKSIGN_BASE_URL=https://sandbox.clicksign.com`),
//   a `requestSignatureKey` retornada é válida APENAS no domínio sandbox — montar
//   o iframe com endpoint=prod resulta em key não encontrada e o iframe nunca
//   carrega (ou carrega com erro Clicksign). A resolução do endpoint é:
//     1. Derivar do `envelopeUrl` retornado pelo backend (origin = sandbox|prod)
//     2. Fallback: env var `VITE_CLICKSIGN_ENDPOINT` (override em dev local)
//     3. Fallback final: `https://app.clicksign.com` (preserva default do SDK).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@alamo/ui';

const CLICKSIGN_SCRIPT_SRC =
  'https://cdn-public-library.clicksign.com/embedded/embedded.min-1.0.0.js';
const CLICKSIGN_ENDPOINT_DEFAULT = 'https://app.clicksign.com';
const CONTAINER_ID = 'clicksign-widget-container';

/**
 * Teto de espera pelo evento `loaded` do SDK Clicksign. Se o iframe não emitir
 * `loaded` dentro desse prazo (CDN bloqueado, key sandbox montada contra origin
 * prod, domínio fora da whitelist do painel Clicksign), o overlay de loading é
 * trocado por um erro inline + botão de retry — em vez de um spinner infinito.
 */
const CLICKSIGN_LOAD_TIMEOUT_MS = 20_000;

/**
 * Resolve o `endpoint` do widget Clicksign (origin do iframe) na seguinte ordem:
 * 1. `envelopeUrl` retornado pelo backend (que reflete `CLICKSIGN_BASE_URL`
 *    sandbox vs prod) — extrai o `origin`.
 * 2. Env var Vite `VITE_CLICKSIGN_ENDPOINT` — override em dev local.
 * 3. Default hardcoded `https://app.clicksign.com` (produção, mesmo default do SDK).
 *
 * Retorna URL sem trailing slash. Defensivo: catch em `new URL` para envelopeUrl
 * inválido (retorna fallback subsequente).
 */
export function resolveClicksignEndpoint(envelopeUrl?: string): string {
  if (envelopeUrl) {
    try {
      return new URL(envelopeUrl).origin;
    } catch {
      // envelopeUrl inválido — cai para próximo fallback.
    }
  }
  const envOverride = import.meta.env.VITE_CLICKSIGN_ENDPOINT;
  if (envOverride && typeof envOverride === 'string' && envOverride.length > 0) {
    return envOverride.replace(/\/+$/, '');
  }
  return CLICKSIGN_ENDPOINT_DEFAULT;
}

// Tipo defensivo do UMD global injetado pelo script CDN.
type ClicksignWidget = {
  endpoint: string;
  origin: string;
  mount: (containerId: string) => void;
  unmount?: () => void;
  on: (event: 'loaded' | 'signed' | 'error', callback: (payload?: unknown) => void) => void;
};

type ClicksignConstructor = new (requestSignatureKey: string) => ClicksignWidget;

declare global {
  interface Window {
    Clicksign?: ClicksignConstructor;
  }
}

export interface ClicksignEmbedWidgetProps {
  requestSignatureKey: string;
  /**
   * URL standalone do envelope no Clicksign (retornado pelo backend em
   * InitiateSignatureResponseDto.envelopeUrl ou SignatureStatusResponseDto.envelopeUrl).
   * Usado para derivar o `endpoint` do iframe (sandbox vs prod) — ver
   * `resolveClicksignEndpoint`. Opcional: quando omitido, fallback é env var
   * `VITE_CLICKSIGN_ENDPOINT` ou default `https://app.clicksign.com`.
   */
  envelopeUrl?: string;
  onSigned: () => void;
  onError: (err: unknown) => void;
  /**
   * Override do teto de espera pelo evento `loaded` (ms). Default
   * `CLICKSIGN_LOAD_TIMEOUT_MS`. Exposto só para testes encurtarem o relógio.
   */
  loadTimeoutMs?: number;
}

/**
 * Lazy-loads the Clicksign embedded SDK from CDN and renders the signature
 * iframe inside a fixed-id container. Cleans up the script on unmount.
 *
 * Re-mount safety: the effect depends on `requestSignatureKey` — when a new
 * envelope key arrives the previous widget is unmounted, the script tag is
 * removed and a fresh instance is created. Multiple instances simultaneously
 * are not supported by the Clicksign SDK (single container id).
 */
export function ClicksignEmbedWidget({
  requestSignatureKey,
  envelopeUrl,
  onSigned,
  onError,
  loadTimeoutMs = CLICKSIGN_LOAD_TIMEOUT_MS,
}: ClicksignEmbedWidgetProps) {
  const { t } = useTranslation('signature');
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<ClicksignWidget | null>(null);
  const [loading, setLoading] = useState(true);
  const [scriptError, setScriptError] = useState(false);

  // Retry NÃO re-monta a mesma `requestSignatureKey`: uma key embed do Clicksign
  // morta (410) re-montada gera o mesmo 410 num loop. Em vez disso, delega ao
  // parent (`onError`) re-emitir uma key fresca via POST /signature/initiate; o
  // novo `requestSignatureKey` prop dispara o re-mount com a key viva. O re-mount
  // do effect acontece só quando a key muda — nunca com a mesma key.
  const handleRetry = useCallback(() => {
    setScriptError(false);
    setLoading(true);
    onError(new Error('clicksign_manual_retry'));
  }, [onError]);

  // Resolve endpoint dinâmico (sandbox vs prod) a partir do envelopeUrl do
  // backend — fix B-NEW-4. Memoizado para estabilidade de deps do useEffect.
  const clicksignEndpoint = useMemo(() => resolveClicksignEndpoint(envelopeUrl), [envelopeUrl]);

  useEffect(() => {
    if (!requestSignatureKey || typeof window === 'undefined') return undefined;

    setLoading(true);
    setScriptError(false);

    // Watchdog: se `loaded` não disparar dentro do teto, troca o spinner por
    // erro inline + retry. Surfaces de config quebrada (CDN/CORS/origin sandbox)
    // que de outra forma deixariam o overlay girando para sempre.
    let settled = false;
    const loadWatchdog = setTimeout(() => {
      if (settled) return;
      settled = true;
      setLoading(false);
      setScriptError(true);
      onError(new Error('clicksign_load_timeout'));
    }, loadTimeoutMs);

    // Re-use existing script tag if already injected (e.g. navigation back
    // & forth). Browser dedupes by src but we double-check defensively.
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${CLICKSIGN_SCRIPT_SRC}"]`,
    );
    const script = existing ?? document.createElement('script');
    let appended = false;

    function failWidget(err: unknown) {
      if (settled) return;
      settled = true;
      clearTimeout(loadWatchdog);
      setScriptError(true);
      setLoading(false);
      onError(err);
    }

    function initWidget() {
      try {
        if (!window.Clicksign) {
          throw new Error('Clicksign SDK not available on window after script load');
        }
        // Limpa qualquer iframe de um mount anterior (StrictMode double-invoke em
        // dev, retry, ou re-render) antes de montar. Sem isso o SDK faz append no
        // mesmo container e empilha widgets duplicados.
        containerRef.current?.replaceChildren();
        const widget = new window.Clicksign(requestSignatureKey);
        widget.endpoint = clicksignEndpoint;
        widget.origin = window.location.origin;
        widget.mount(CONTAINER_ID);
        widget.on('loaded', () => {
          if (settled) return;
          settled = true;
          clearTimeout(loadWatchdog);
          setLoading(false);
        });
        widget.on('signed', () => onSigned());
        widget.on('error', (err: unknown) => failWidget(err));
        widgetRef.current = widget;
      } catch (err) {
        failWidget(err);
      }
    }

    if (existing && window.Clicksign) {
      // Script already present + SDK ready → mount imediato.
      initWidget();
    } else {
      script.src = CLICKSIGN_SCRIPT_SRC;
      script.async = true;
      script.onload = () => initWidget();
      script.onerror = (err) => failWidget(err);
      if (!existing) {
        document.body.appendChild(script);
        appended = true;
      }
    }

    return () => {
      clearTimeout(loadWatchdog);
      // Cleanup widget instance (idempotent SDK).
      try {
        widgetRef.current?.unmount?.();
      } catch {
        // SDK may not support unmount across all versions — best effort.
      }
      widgetRef.current = null;
      // Remove the script tag only if WE appended it (avoid yanking a tag
      // another component may still be using).
      if (appended && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [requestSignatureKey, clicksignEndpoint, onSigned, onError, loadTimeoutMs]);

  return (
    <div className="relative w-full" data-testid="clicksign-embed-widget">
      {loading && !scriptError && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-white"
          data-testid="clicksign-loading"
        >
          <div className="flex flex-col items-center gap-2 text-ink/50">
            <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
            <p className="text-sm">{t('messages.iframe_loading')}</p>
          </div>
        </div>
      )}
      {scriptError && (
        <div
          className="flex flex-col gap-3 rounded-lg border border-erro-apoio/20 bg-erro-apoio/5 p-3 text-sm text-erro-apoio"
          role="alert"
          data-testid="clicksign-error"
        >
          <span>{t('messages.iframe_error')}</span>
          <Button
            variant="secondary"
            type="button"
            onClick={handleRetry}
            data-testid="clicksign-retry"
          >
            {t('actions.retry')}
          </Button>
        </div>
      )}
      {/* Altura EXPLÍCITA (não min-h): o iframe do SDK é height:100% e percentual
          de altura só resolve contra um height definido — com min-height o iframe
          colapsa (~150px) e o conteúdo do Clicksign fica cortado. max-h responsivo
          evita overflow em telas baixas (o Sheet é max-h-[90dvh]). */}
      <div id={CONTAINER_ID} ref={containerRef} className="h-[600px] max-h-[78dvh] w-full" />
    </div>
  );
}
